import type { Sql } from '../db/tipos';
import type { Bbox } from '../geo/geometria';
import { teselarBbox, bboxTesela, type Tesela } from '../geo/teselado';
import { descargarCursosAgua, cursoAFila, DIST_MAX_AGUA_M } from './hidrografia';
import { insertarCursosAgua, registrarTesela, teselasVigentes, actualizarDistanciasAgua } from '../db/consultas';
import type { Progreso } from '../db/protocolo';

/**
 * Carga de la hidrografia de una zona: teselas de 5 km alineadas a la malla (los rios son pocos
 * y largos; 1 km seria una peticion por casi nada) sobre la caja de la zona ampliada con la
 * distancia maxima que se mide, para que una parcela del borde tambien encuentre su rio. Al
 * terminar se recalcula la distancia al agua de todas las parcelas de la zona. Corre en el
 * worker y en los tests (fetch inyectable).
 */
export const LADO_TESELA_HIDRO_M = 5000;

export function teselasHidro(bbox: Bbox, epsg: number): Tesela[] {
  const m = DIST_MAX_AGUA_M;
  return teselarBbox([bbox[0] - m, bbox[1] - m, bbox[2] + m, bbox[3] + m], epsg, LADO_TESELA_HIDRO_M)
    .map((t) => ({ ...t, id: `hy_${t.id}` }));
}

export interface OpcionesHidro {
  bbox: Bbox;
  epsg: number;
  dias: number;
  ahora?: number;
  f?: typeof fetch;
  senal?: AbortSignal;
  onProgreso?: (p: Progreso) => void;
}

export interface ResultadoHidro {
  descargadas: number;
  reutilizadas: number;
  cursos: number;
  parcelasActualizadas: number;
  errores: string[];
  cancelado: boolean;
}

export async function cargarHidrografia(sql: Sql, op: OpcionesHidro): Promise<ResultadoHidro> {
  const ahora = op.ahora ?? Date.now();
  const teselas = teselasHidro(op.bbox, op.epsg);
  const vigentes = teselasVigentes(sql, 'hy', ahora, op.dias);
  const pendientes = teselas.filter((t) => !vigentes.has(t.id));
  const r: ResultadoHidro = { descargadas: 0, reutilizadas: teselas.length - pendientes.length, cursos: 0, parcelasActualizadas: 0, errores: [], cancelado: false };
  let hechas = 0;
  const informar = (tesela: string | null, registros: number, terminado: boolean) => {
    op.onProgreso?.({ hechas, total: pendientes.length, capa: 'hy', tesela, registros, errores: [...r.errores], terminado, cancelado: r.cancelado });
  };
  informar(null, 0, false);
  let indice = 0;
  const trabajador = async () => {
    while (indice < pendientes.length) {
      if (op.senal?.aborted) { r.cancelado = true; return; }
      const t = pendientes[indice++];
      let n = 0;
      try {
        const cursos = await descargarCursosAgua([t.minx, t.miny, t.maxx, t.maxy], t.epsg, { f: op.f, senal: op.senal });
        const filas = cursos.map((c) => cursoAFila(c, t.epsg, t.id, ahora)).filter((x): x is NonNullable<typeof x> => x !== null);
        insertarCursosAgua(sql, filas);
        n = filas.length;
        registrarTesela(sql, { id: t.id, capa: 'hy', minx: t.minx, miny: t.miny, maxx: t.maxx, maxy: t.maxy, epsg: t.epsg, cargadaEn: ahora, nRegistros: n });
        r.cursos += n;
        r.descargadas++;
      } catch (e) {
        r.errores.push(`hy ${t.id} (${bboxTesela(t)}): ${e instanceof Error ? e.message : String(e)}`);
      }
      hechas++;
      informar(t.id, n, false);
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(2, pendientes.length || 1)) }, trabajador));
  if (!r.cancelado) r.parcelasActualizadas = actualizarDistanciasAgua(sql, op.bbox, DIST_MAX_AGUA_M);
  informar(null, 0, true);
  return r;
}
